<template>
	<FormKitProvider :config="formKitConfig">
		<div v-if="!loaded">Loading...</div>
		<AdminView v-else-if="auth.showAdminPage" />
		<ManagerView v-else-if="auth.showManagerPage" />
		<RouterView v-else-if="auth.user?.salesContact" />
		<LoginView v-else />
		<login-modal />
	</FormKitProvider>
</template>

<script setup lang="ts">
import { appLocale } from './i18n'
import { useCalculatorStore } from './stores/calculator'
import { useContentStore } from './stores/content'
import { useCrmStore } from './stores/crm'
import { useEncryptionStore } from './stores/encryption'
import { useSessionStore } from './stores/session'
import { useUserSettingsStore } from './stores/settings'
import { type SyncOptions, useSyncStore } from './stores/sync'
import AdminView from './views/AdminView.vue'
import ManagerView from './views/ManagerView.vue'
import { localizedConfig } from '@/../formkit.config'
import LoginModal from '@/components/common/LoginModal.vue'
import { handlePrintSlideVisibility } from '@/plugins/handlePrintSlideVisibility'
import { useDocumentStore } from '@/stores//document'
import { useAuthStore } from '@/stores/auth'
import LoginView from '@/views/LoginView.vue'
import { type FormKitOptions } from '@formkit/core'
import { FormKitProvider } from '@formkit/vue'
import { captureException, setUser } from '@sentry/vue'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

/** Handle visibility of slides when printing **/
handlePrintSlideVisibility()

/*** Data ***/
const auth = useAuthStore()
const loaded = ref(false)

/** Config to localize FormKit according to the App's language */
const formKitConfig = computed<FormKitOptions>(() => localizedConfig(appLocale))

const route = useRoute()

onMounted(async () => {
	const crm = useCrmStore()
	const session = useSessionStore()
	const content = useContentStore()
	const documentStore = useDocumentStore()
	const sync = useSyncStore()
	const userSettings = useUserSettingsStore()

	const localUser = auth.getUserFromLocal()
	const serverUser = auth.getUserFromServer()

	// Check if the user is on Windows
	const isWindows = navigator.userAgent.indexOf('Win') !== -1

	// Apply the scrollbar windows class based on whether or not it is Windows
	const appElement = document.getElementById('app')
	if (appElement) {
		if (isWindows) {
			appElement.classList.add('windows')
		} else {
			appElement.classList.remove('windows')
		}
	}

	// Load the latest data file in background
	useCalculatorStore().getDataFileFromStorage()

	// Start fetching latest server key
	const serverEncryptionKeyPromise =
		useEncryptionStore().fetchUpdatedServerKey()

	// Load data. Continue if one auth attempt succeeds
	await Promise.allSettled([localUser, serverUser])

	setUser({ id: auth.user?.hub.id, email: auth.user?.hub.email })

	// On page load, reverify all cached assets
	const initialLoadOptions: SyncOptions = {
		verify: true,
	}

	if (auth.user?.salesContact) {
		// Migrate sessions once we have a server key
		await serverEncryptionKeyPromise.catch(() => {})
		await session.migrateSessions()

		// Migrate documents once sessions are all up to date
		await documentStore.migrateDocuments()

		await Promise.all([
			auth.getStoryBlokToken().catch((e) => captureException(e)),
			crm.getPriceBooksFromStorage(),
			crm.getAppointmentsFromStorage(),
			crm.getFuneralHomesFromStorage(),
			session.loadAll(),
			content.loadDecksFromCache(),
			content.loadFHGroupMediasFromCache(),
			userSettings.loadSettings(),
		])

		// Start getting thumbnails from cache and fetch any missing assets from server
		content.fetchMissingAssets(initialLoadOptions)

		// determine whether studio is used in FDL
		// a ref for syncStore to be aware on what type of sync to run
		// TODO: Refine after auth is available: PSS-2055
		sync.isFdl = !!route.meta.embedded

		// Try uploading all our stuff and downloading new stuff
		sync.beginSync(initialLoadOptions)
	}

	loaded.value = true
})
</script>
